/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MultimodalLiveAPIClientConnection,
  MultimodalLiveClient,
} from "../../../lib/multimodal-live-client";
import { LiveConfig } from "../../../multimodal-live-types";
import { getAudioStreamer } from "../contexts/InterviewContext";
import { audioContext, base64ToArrayBuffer, base64ToFloat32Array } from "../../../lib/utils";
import { GenerativeContentBlob } from "@google/generative-ai";
import { getInterviewSystemPrompt } from "../../../constants/interview-prompts";
import { v4 as uuidv4 } from 'uuid';
import { GEMINI_VOICES } from '../../../constants/gemini';

// Define an extended client type that includes sendRealtimeInput with the updated format
export interface ExtendedMultimodalLiveClient extends Omit<MultimodalLiveClient, 'sendRealtimeInput'> {
  // Using updated format for sendRealtimeInput
  sendRealtimeInput: (data: GenerativeContentBlob[] | { 
    audio?: { mimeType: string, data: string },
    video?: { mimeType: string, data: string },
    text?: string
  }) => void;
}

export type UseLiveAPIResults = {
  client: ExtendedMultimodalLiveClient;
  setConfig: (config: LiveConfig) => void;
  config: LiveConfig;
  connected: boolean;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  volume: number;
};

// Global WebSocket instance to be shared across all components
const globalWsInstance: {
  ws: WebSocket | null;
  isConnecting: boolean;
  clientId: string | null; // ID of the client instance that owns the connection
  url: string | null;
  config: LiveConfig | null;
  connectionLock: boolean; // Prevent new connections during cooldown
  lockUntil: number | null; // Timestamp until the lock expires
} = {
  ws: null,
  isConnecting: false,
  clientId: null,
  url: null,
  config: null,
  connectionLock: false,
  lockUntil: null,
};

// --- OFFICIAL GOOGLE IMPLEMENTATION: Audio Streamer Queue ---
interface AudioStreamerState {
  audioContext: AudioContext | null;
  audioQueue: Float32Array[];
  isPlaying: boolean;
  endOfQueueAudioSource: AudioBufferSourceNode | null;
  isStreamComplete: boolean;
  checkInterval: number | null;
  scheduledTime: number;
  initialBufferTime: number;
  sampleRate: number;
  bufferSize: number;
  gainNode: GainNode | null;
  isInitializing: boolean;
  initializationPromise: Promise<AudioContext | null> | null;
}

const audioStreamerState: AudioStreamerState = {
  audioContext: null,
  audioQueue: [],
  isPlaying: false,
  endOfQueueAudioSource: null,
  isStreamComplete: false,
  checkInterval: null,
  scheduledTime: 0,
  initialBufferTime: 0.1, // 100ms - per Google's implementation
  sampleRate: 24000,
  bufferSize: 7680, // Per Google: 320ms at 24kHz
  gainNode: null,
  isInitializing: false,
  initializationPromise: null,
};

// Expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).globalWsInstance = globalWsInstance;
  (window as any).audioStreamerState = audioStreamerState;
}

// Function to add the state change listener
function addAudioContextStateListener(context: AudioContext) {
  context.onstatechange = () => {
    console.log(`[AudioStreamer] Context state changed to: ${context.state}`);
    if (context.state === 'suspended') {
      context.resume().catch(err => console.error('[AudioStreamer] Error auto-resuming:', err));
    }
  };
}

// Initialize audio context - GOOGLE IMPLEMENTATION STYLE
async function ensureAudioContext(): Promise<AudioContext | null> {
  if (audioStreamerState.audioContext && audioStreamerState.audioContext.state !== 'closed') {
    if (audioStreamerState.audioContext.state === 'suspended') {
      try {
        await audioStreamerState.audioContext.resume();
        console.log('[AudioStreamer] ✅ Context resumed');
      } catch (err) {
        console.error('[AudioStreamer] Error resuming:', err);
      }
    }
    return audioStreamerState.audioContext;
  }

  if (audioStreamerState.isInitializing && audioStreamerState.initializationPromise) {
    return audioStreamerState.initializationPromise;
  }

  audioStreamerState.isInitializing = true;

  audioStreamerState.initializationPromise = (async () => {
    try {
      const ctx = await audioContext({ sampleRate: 24000 });
      console.log(`[AudioStreamer] ✅ AudioContext initialized (${ctx.sampleRate}Hz)`);
      audioStreamerState.audioContext = ctx;
      audioStreamerState.gainNode = ctx.createGain();
      audioStreamerState.gainNode.connect(ctx.destination);
      addAudioContextStateListener(ctx);
      audioStreamerState.isInitializing = false;
      return ctx;
    } catch (error) {
      console.error("[AudioStreamer] Error creating context:", error);
      audioStreamerState.isInitializing = false;
      audioStreamerState.initializationPromise = null;
      return null;
    }
  })();

  return audioStreamerState.initializationPromise;
}

// GOOGLE OFFICIAL: Process PCM16 to Float32
function processPCM16Chunk(chunk: Uint8Array): Float32Array {
  const float32Array = new Float32Array(chunk.length / 2);
  const dataView = new DataView(chunk.buffer, chunk.byteOffset, chunk.byteLength);

  for (let i = 0; i < chunk.length / 2; i++) {
    const int16 = dataView.getInt16(i * 2, true); // true = littleEndian
    float32Array[i] = int16 / 32768;
  }
  return float32Array;
}

// GOOGLE OFFICIAL: Add PCM16 chunks to queue
async function addPCM16(chunk: Uint8Array) {
  const context = await ensureAudioContext();
  if (!context) {
    console.warn('[AudioStreamer] Context unavailable');
    return;
  }

  audioStreamerState.isStreamComplete = false;

  // Convert PCM16 to Float32
  let processingBuffer = processPCM16Chunk(chunk);

  // Split into bufferSize chunks
  while (processingBuffer.length >= audioStreamerState.bufferSize) {
    const buffer = processingBuffer.slice(0, audioStreamerState.bufferSize);
    audioStreamerState.audioQueue.push(buffer);
    processingBuffer = processingBuffer.slice(audioStreamerState.bufferSize);
  }

  // Add remaining
  if (processingBuffer.length > 0) {
    audioStreamerState.audioQueue.push(processingBuffer);
  }

  // Start playback
  if (!audioStreamerState.isPlaying) {
    audioStreamerState.isPlaying = true;
    audioStreamerState.scheduledTime = context.currentTime + audioStreamerState.initialBufferTime;
    console.log(`[AudioStreamer] ▶️ Starting playback, scheduled for ${audioStreamerState.scheduledTime.toFixed(3)}s`);
    scheduleNextBuffer();
  }
}

// GOOGLE OFFICIAL: Create AudioBuffer from Float32Array
function createAudioBuffer(audioData: Float32Array): AudioBuffer {
  const context = audioStreamerState.audioContext!;
  const audioBuffer = context.createBuffer(1, audioData.length, audioStreamerState.sampleRate);
  audioBuffer.getChannelData(0).set(audioData);
  return audioBuffer;
}

// GOOGLE OFFICIAL: Schedule next buffer with intelligent ahead-of-time scheduling
function scheduleNextBuffer() {
  const context = audioStreamerState.audioContext;
  if (!context || !audioStreamerState.gainNode) return;

  const SCHEDULE_AHEAD_TIME = 0.2; // Schedule 200ms ahead per Google

  while (
    audioStreamerState.audioQueue.length > 0 &&
    audioStreamerState.scheduledTime < context.currentTime + SCHEDULE_AHEAD_TIME
  ) {
    const audioData = audioStreamerState.audioQueue.shift()!;
    const audioBuffer = createAudioBuffer(audioData);
    const source = context.createBufferSource();

    // Track end of queue
    if (audioStreamerState.audioQueue.length === 0) {
      if (audioStreamerState.endOfQueueAudioSource) {
        audioStreamerState.endOfQueueAudioSource.onended = null;
      }
      audioStreamerState.endOfQueueAudioSource = source;
      source.onended = () => {
        if (
          audioStreamerState.audioQueue.length === 0 &&
          audioStreamerState.endOfQueueAudioSource === source
        ) {
          audioStreamerState.endOfQueueAudioSource = null;
          console.log('[AudioStreamer] ✅ Playback complete');
          audioStreamerState.isPlaying = false;
        }
      };
    }

    source.buffer = audioBuffer;
    source.connect(audioStreamerState.gainNode);

    // Schedule in future, never in past
    const startTime = Math.max(audioStreamerState.scheduledTime, context.currentTime);
    source.start(startTime);

    audioStreamerState.scheduledTime = startTime + audioBuffer.duration;
  }

  // Keep checking if queue gets data
  if (audioStreamerState.audioQueue.length === 0) {
    if (audioStreamerState.isStreamComplete) {
      audioStreamerState.isPlaying = false;
      if (audioStreamerState.checkInterval) {
        clearInterval(audioStreamerState.checkInterval);
        audioStreamerState.checkInterval = null;
      }
    } else {
      if (!audioStreamerState.checkInterval) {
        audioStreamerState.checkInterval = window.setInterval(() => {
          if (audioStreamerState.audioQueue.length > 0) {
            scheduleNextBuffer();
          }
        }, 100) as unknown as number;
      }
    }
  } else {
    // Schedule next check
    const nextCheckTime = (audioStreamerState.scheduledTime - context.currentTime) * 1000;
    setTimeout(() => scheduleNextBuffer(), Math.max(0, nextCheckTime - 50));
  }
}

// GOOGLE OFFICIAL: Stop/interrupt function
function stopAudioStreamer() {
  console.log('[AudioStreamer] 🛑 Stopping audio stream');
  audioStreamerState.isPlaying = false;
  audioStreamerState.isStreamComplete = true;
  audioStreamerState.audioQueue = [];
  audioStreamerState.scheduledTime = audioStreamerState.audioContext?.currentTime || 0;

  if (audioStreamerState.checkInterval) {
    clearInterval(audioStreamerState.checkInterval);
    audioStreamerState.checkInterval = null;
  }

  if (audioStreamerState.gainNode && audioStreamerState.audioContext) {
    audioStreamerState.gainNode.gain.linearRampToValueAtTime(0, audioStreamerState.audioContext.currentTime + 0.1);
    setTimeout(() => {
      if (audioStreamerState.gainNode && audioStreamerState.audioContext) {
        audioStreamerState.gainNode.disconnect();
        audioStreamerState.gainNode = audioStreamerState.audioContext.createGain();
        audioStreamerState.gainNode.connect(audioStreamerState.audioContext.destination);
      }
    }, 200);
  }

  console.log('[AudioStreamer] ✅ Audio stream stopped');
}

// Complete stream function
function completeAudioStream() {
  console.log('[AudioStreamer] 🏁 Marking stream as complete');
  audioStreamerState.isStreamComplete = true;
}

// Expose functions on window for emergency use
if (typeof window !== 'undefined') {
  (window as any).stopAudioStreamer = stopAudioStreamer;
  (window as any).completeAudioStream = completeAudioStream;
}

// --- End Audio Streamer Implementation ---

// At the top of the file, add a function to retrieve the persistent client ID
function getPersistentClientId(): string {
  try {
    let clientId = sessionStorage.getItem('mocktrail_websocket_client_id');
    if (!clientId) {
      clientId = uuidv4();
      sessionStorage.setItem('mocktrail_websocket_client_id', clientId);
    }
    return clientId;
  } catch (e) {
    // Fallback to a temporary ID if session storage is not available
    return uuidv4();
  }
}

// Function to initialize the global audio context
const initializeGlobalAudioContext = async () => {
  try {
    await ensureAudioContext();
    // console.log('[GlobalAudioPlayer] Audio context initialized.');
  } catch (err) {
    console.error('[GlobalAudioPlayer] Error initializing audio context:', err);
  }
};

export function useLiveAPI({
  url,
  apiKey,
  interviewDetails,
  implementorUniqueId,
}: MultimodalLiveAPIClientConnection): UseLiveAPIResults & { interruptPlayback: () => void } {
  const [connected, setConnected] = useState(false);
  const [client, setClient] = useState<ExtendedMultimodalLiveClient | null>(null);
  const [config, setConfig] = useState<LiveConfig | null>(null);
  const [volume, setVolume] = useState(1);
  const connectionAttemptRef = useRef(0);
  
  // Use a persistent reference to track this instance
  const connectionInstanceIdRef = useRef<string>(`conn-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`);
  
  // Get the persistent client ID for WebSocket connections
  const persistentClientIdRef = useRef<string>(getPersistentClientId());
  
  // Save original URL for reference
  const originalUrlRef = useRef<string>(url || '');
  
  // Reference to timeout to prevent memory leaks
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Connection timeout reference
  const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Connection debounce reference
  const connectionDebounceRef = useRef<{
    isConnecting: boolean;
    pendingDebounceTimer: NodeJS.Timeout | null;
  }>({
    isConnecting: false,
    pendingDebounceTimer: null
  });
  
  // Reference for managing setup completion
  const setupCompletionRef = useRef<{
    wsReference: WebSocket | null;
    inProgress: boolean;
    completed: boolean;
  }>({
    wsReference: null,
    inProgress: false,
    completed: false
  });
  
  // Function to handle setup completion
  const onSetupComplete = useCallback(() => {
    console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Setup completed`);
    setConnected(true);
    setupCompletionRef.current.completed = true;
    setupCompletionRef.current.inProgress = false;
  }, [connectionInstanceIdRef]);

  // Initialize the client
  useEffect(() => {
    // Ensure audio context is initialized
    initializeGlobalAudioContext();
    
    // Use existing client if it's already connected (this helps prevent multiple connections)
    if (client && client.ws && client.ws.readyState === WebSocket.OPEN) {
      console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Client already exists and is connected`);
      setConnected(true);
      return;
    }
    
    // Create client with the persistent client ID
    console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Initializing client with persistent ID: ${persistentClientIdRef.current}`);
    
    try {
      const newClient = new MultimodalLiveClient({
        url: originalUrlRef.current,
        apiKey,
        interviewDetails,
        implementorUniqueId
      });
      
      // Store reference to client
      setClient(newClient);
      
      // Set up client event listeners
      setupClientEventListeners(newClient);
      
      // Check for model to use
      const model = interviewDetails?.model || 'models/gemini-2.0-flash-exp';
      console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Configured with model: ${model}`);
      
      // Get the properly formatted system prompt from the interview-prompts utility
      const systemPrompt = getInterviewSystemPrompt(interviewDetails);
      console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Using system prompt from interview-prompts (${systemPrompt.length} chars)`);
      
      setConfig({
        model: model,
        generationConfig: {
          responseModalities: "audio",
          temperature: 0.7,
          maxOutputTokens: 8192,
          candidateCount: 1,
          stopSequences: [],
          topP: 0.95,
          topK: 32,
          presencePenalty: 0,
          frequencyPenalty: 0,
          speechConfig: {
            voiceConfig: {
              prebuiltVoiceConfig: {
                voiceName: interviewDetails?.voicePack || GEMINI_VOICES.Puck
              }
            }
          }
        },
        systemInstruction: {
          role: "system",
          parts: [{
            text: systemPrompt // Use the formatted system prompt from the utility function
          },
          ...(interviewDetails?.interviewPrompt ? [{
            text: interviewDetails?.interviewPrompt
          }] : [])
        ]
        }
        // Note: VAD (Voice Activity Detection) settings are handled automatically by Gemini Live API
        // The following configuration is kept for documentation purposes but won't be applied
        // The API manages speech detection, interruption handling, and turn-taking internally
        /*
        voiceActivityDetection: {
          enabled: true,
          endOfSpeechSensitivity: 0.8, // Would prevent half-word cutoffs
          startOfSpeechSensitivity: 0.3, // Would prevent premature responses
          silenceDurationMs: 1500, // Would allow natural pauses
          prefixPaddingMs: 500, // Would capture speech beginning
          automaticActivityDetection: true
        },
        allowInterruptions: false, // API handles this automatically
        turnCoverage: "full" // API handles this automatically
        */
      });
    } catch (err) {
      console.error(`[useLiveAPI] [${connectionInstanceIdRef.current}] Error initializing client:`, err);
    }
    
    // Cleanup function
    return () => {
      disconnect();
    };
  }, [url, apiKey, interviewDetails]);
  
  // Function to setup event listeners for the client
  const setupClientEventListeners = useCallback((newClient: MultimodalLiveClient) => {
    // Setup client event listeners if needed
    console.log(`[useLiveAPI] Adding setupcomplete listener to client`);
    
    try {
      newClient.on('setupcomplete', () => {
        console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Setup completed successfully`);
        setConnected(true);
        
        // Store connection status in session storage for recovery
        try {
          sessionStorage.setItem('websocket_connection_active', 'true');
          sessionStorage.setItem('websocket_connection_time', Date.now().toString());
          sessionStorage.setItem('websocket_connection_id', connectionInstanceIdRef.current);
        } catch (err) {
          console.warn(`[useLiveAPI] [${connectionInstanceIdRef.current}] Cannot access session storage:`, err);
        }
        
        // Send event to UI
        try {
          window.dispatchEvent(new CustomEvent('websocket_connected'));
        } catch (err) {
          // Ignore errors
        }
        
        console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Connection fully established and ready`);
      });
    } catch (err) {
      console.error(`[useLiveAPI] Error setting up event listeners:`, err);
    }
  }, []);

  // Expose the interrupt function (GOOGLE OFFICIAL STYLE)
  const interruptPlayback = useCallback(() => {
      stopAudioStreamer();
  }, []);

  // Main event handling effect
  useEffect(() => {
    const onClose = (ev: CloseEvent) => {
      console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] WebSocket closed:`, ev);
      
      // Always update connected state immediately
      setConnected(false);
      
      // Reset connection flags
      connectionDebounceRef.current.isConnecting = false;
      
      // Clear the backup WebSocket reference since it's now closed
      setupCompletionRef.current.wsReference = null;
      setupCompletionRef.current.inProgress = false;
      setupCompletionRef.current.completed = false;
      
      // Check for quota exceeded error - look at both code and reason for better detection
      const isQuotaError = 
        (ev.code === 1011 || ev.code === 1008) && 
        (ev.reason?.toLowerCase().includes('quota') || 
         ev.reason?.toLowerCase().includes('exceeded') || 
         ev.reason?.toLowerCase().includes('limit'));
         
      if (isQuotaError) {
        console.error(`[useLiveAPI] [${connectionInstanceIdRef.current}] Quota exceeded error detected:`, ev.reason);
        
        // Set the quota exceeded flag in memory using the static method
        if (typeof MultimodalLiveClient.setQuotaExceeded === 'function') {
          MultimodalLiveClient.setQuotaExceeded(true);
          
          // Store marker in session storage for persistence
          try {
            sessionStorage.setItem('gemini_quota_exceeded', 'true');
            sessionStorage.setItem('gemini_quota_exceeded_time', Date.now().toString());
            console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Stored quota exceeded marker in session storage`);
          } catch (err) {
            console.warn(`[useLiveAPI] [${connectionInstanceIdRef.current}] Unable to store quota exceeded marker:`, err);
          }
        }
        
        // Clean up any connection state storage
        try {
          sessionStorage.removeItem('websocket_connection_active');
          sessionStorage.removeItem('websocket_connection_time');
          sessionStorage.removeItem('websocket_connection_id');
        } catch (err) {
          // Ignore errors
        }
        
        // Create a standardized custom event for quota exceeded errors
        const quotaExceededEvent = new CustomEvent('geminiQuotaExceeded', {
          detail: {
            message: ev.reason || 'Quota exceeded error detected',
            code: ev.code,
            source: 'useLiveAPI.onClose'
          }
        });
        
        // Dispatch the event globally
        setTimeout(() => {
          console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Dispatching standardized quota exceeded event`);
          window.dispatchEvent(quotaExceededEvent);
        }, 100);
      } else {
        // For other closure reasons, just cleanup any stale connection state
        try {
          const storedConnectionId = sessionStorage.getItem('websocket_connection_id');
          if (storedConnectionId === connectionInstanceIdRef.current) {
            console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Cleaning up connection state for current instance`);
            sessionStorage.removeItem('websocket_connection_active');
            sessionStorage.removeItem('websocket_connection_time');
            sessionStorage.removeItem('websocket_connection_id');
          }
        } catch (err) {
          // Ignore errors
        }
      }
      interruptPlayback(); // Stop playback and clear queue on close
    };

    const onContent = (content: any) => {
      console.log("[useLiveAPI] Received content:", content);
      // TODO: Handle text content display if necessary
    };

    const onCleanupAudio = () => {
      console.log("[useLiveAPI] Received cleanup_audio event - stopping all audio playback");
      stopAudioStreamer(); // GOOGLE OFFICIAL STYLE
      completeAudioStream();
    };

    // Add event listeners (Removed 'audio' listener)
    if (client) {
      client
        .on("close", onClose)
        .on("content", onContent)
        .on("cleanup_audio", onCleanupAudio);
    }
    // setupcomplete is handled in its own effect for better stability

    // Return cleanup function
    return () => {
      if (client) {
        client
          .off("close", onClose)
          .off("content", onContent)
          .off("cleanup_audio", onCleanupAudio);
      }
      
      // Also clean up any timeouts
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
    };
  }, [client, interruptPlayback]);

  // Check for quota exceeded in session storage on mount
  useEffect(() => {
    try {
      const quotaExceeded = sessionStorage.getItem('gemini_quota_exceeded');
      const quotaExceededTime = sessionStorage.getItem('gemini_quota_exceeded_time');
      
      if (quotaExceeded === 'true' && quotaExceededTime) {
        const exceedTime = parseInt(quotaExceededTime, 10);
        const now = Date.now();
        const hoursPassed = (now - exceedTime) / (1000 * 60 * 60);
        
        // Only consider quota exceeded valid for 6 hours
        if (hoursPassed < 6) {
          console.log(`[useLiveAPI] Found quota exceeded marker from ${hoursPassed.toFixed(1)} hours ago`);
          
          // Set the flag
          if (typeof MultimodalLiveClient.setQuotaExceeded === 'function') {
            MultimodalLiveClient.setQuotaExceeded(true);
          }
        } else {
          console.log(`[useLiveAPI] Found expired quota exceeded marker (${hoursPassed.toFixed(1)} hours old), clearing`);
          sessionStorage.removeItem('gemini_quota_exceeded');
          sessionStorage.removeItem('gemini_quota_exceeded_time');
        }
      }
    } catch (err) {
      console.warn('[useLiveAPI] Error checking quota exceeded in session storage:', err);
    }
  }, []);

  // First declare the disconnect function
  const disconnect = useCallback(async () => {
    console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Disconnecting WebSocket`);
    
    // FIRST PRIORITY: Stop all audio playback immediately (GOOGLE OFFICIAL STYLE)
    stopAudioStreamer();
    completeAudioStream();
    
    // Check if this is a disconnect before a force reconnect
    let isBeforeForceReconnect = false;
    try {
      // Try to access the location state from window.history
      const locationState = window.history.state?.usr?.state;
      isBeforeForceReconnect = locationState?._forceReconnect === true;
      
      if (isBeforeForceReconnect) {
        console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Disconnecting before force reconnect - skipping connection lock`);
      }
    } catch (err) {
      console.warn(`[useLiveAPI] Error checking for force reconnect during disconnect:`, err);
    }
    
    // Clear any pending connection timeout
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
    
    // Clear any pending debounce timer
    if (connectionDebounceRef.current.pendingDebounceTimer) {
      clearTimeout(connectionDebounceRef.current.pendingDebounceTimer);
      connectionDebounceRef.current.pendingDebounceTimer = null;
    }
    
    // Always close the WebSocket connection properly, even if it's the global instance
    if (client && client.ws) {
      // If this is the owner of the global WebSocket, clean up global state
      if (client.ws === globalWsInstance.ws) {
        console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] This client owns the global WebSocket - cleaning up global state`);
        
        // Reset global state
        globalWsInstance.isConnecting = false;
        globalWsInstance.clientId = "";
        globalWsInstance.url = "";
        
        // Enforce a connection lock to prevent immediate reconnect (but skip if force reconnect is coming)
        if (!isBeforeForceReconnect) {
          globalWsInstance.connectionLock = true;
          globalWsInstance.lockUntil = Date.now() + 10000; // 10 seconds
        } else {
          // Make sure lock is cleared if we're doing a force reconnect
          globalWsInstance.connectionLock = false;
          globalWsInstance.lockUntil = null;
        }
      }
      
      // Always attempt to close the WebSocket
      try {
        if (client.ws.readyState === WebSocket.OPEN || client.ws.readyState === WebSocket.CONNECTING) {
          console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Forcefully closing WebSocket connection`);
          client.ws.close(1000, "Interview restarting");
        }
      } catch (err) {
        console.warn(`[useLiveAPI] [${connectionInstanceIdRef.current}] Error during disconnect:`, err);
      }
      
      // Reset the global WebSocket reference
      if (globalWsInstance.ws === client.ws) {
        globalWsInstance.ws = null;
      }
      
      // Clear client reference
      client.ws = null;
    }
    
    // Clean up session storage state
    try {
      const storedConnectionId = sessionStorage.getItem('websocket_connection_id');
      // Only clear if this is our connection
      if (storedConnectionId === connectionInstanceIdRef.current) {
        sessionStorage.removeItem('websocket_connection_active');
        sessionStorage.removeItem('websocket_connection_time');
        sessionStorage.removeItem('websocket_connection_id');
      }
      
      // Clear the interview session ID
      sessionStorage.removeItem('interview_session_id');
    } catch (err) {
      console.warn(`[useLiveAPI] [${connectionInstanceIdRef.current}] Error clearing session storage:`, err);
    }
    
    // Update UI state
    setConnected(false);
    
    // Reset connection attempt counter
    connectionAttemptRef.current = 0;

    console.log('[useLiveAPI] Disconnect complete - all audio stopped and resources cleaned up');
  }, [client, connectionInstanceIdRef, interruptPlayback]);

  // Modify the connect function to use the global instance
  const connect = useCallback(async (): Promise<void> => {
    console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Attempting to connect...`);

    if (!client) {
      console.error(`[useLiveAPI] [${connectionInstanceIdRef.current}] Client not initialized, cannot connect.`);
      return Promise.reject(new Error("Client not initialized"));
    }

    // Check if this is a forced reconnect after restart
    let isForceReconnect = false;
    try {
      // Try to access the location state from window.history
      const locationState = window.history.state?.usr?.state;
      isForceReconnect = locationState?._forceReconnect === true;
      
      if (isForceReconnect) {
        console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Force reconnect flag detected - bypassing connection locks`);
        // Reset the connection lock when force reconnecting
        globalWsInstance.connectionLock = false;
        globalWsInstance.lockUntil = null;
        globalWsInstance.isConnecting = false;
      }
    } catch (err) {
      console.warn(`[useLiveAPI] Error checking for force reconnect:`, err);
    }

    // Detect abnormal connection states and reset them
    // This helps prevent issues where a component didn't clean up properly
    try {
      const now = Date.now();
      const lockTime = globalWsInstance.lockUntil || 0;
      const lockDuration = lockTime - now;
      
      // If lock has been active for more than 30 seconds, something is wrong
      if (globalWsInstance.connectionLock && lockDuration > 30000) {
        console.warn(`[useLiveAPI] [${connectionInstanceIdRef.current}] Detected stale connection lock (${lockDuration/1000}s), resetting`);
        globalWsInstance.connectionLock = false;
        globalWsInstance.lockUntil = null;
      }
      
      // If connecting flag is stuck for more than 20 seconds, something is wrong
      if (globalWsInstance.isConnecting && globalWsInstance.ws && 
          globalWsInstance.ws.readyState !== WebSocket.CONNECTING &&
          globalWsInstance.ws.readyState !== WebSocket.OPEN) {
        console.warn(`[useLiveAPI] [${connectionInstanceIdRef.current}] Detected stale connecting state, resetting`);
        globalWsInstance.isConnecting = false;
      }
    } catch (err) {
      console.warn(`[useLiveAPI] Error checking for abnormal connection states:`, err);
    }

    // Prevent connection spamming (skip if force reconnecting)
    if (!isForceReconnect && globalWsInstance.isConnecting) {
      console.warn(`[useLiveAPI] [${connectionInstanceIdRef.current}] Connection attempt ignored: Already connecting.`);
      return Promise.resolve(); // Or reject, depending on desired behavior
    }

    // Check connection lock (skip if force reconnecting)
    if (!isForceReconnect && globalWsInstance.connectionLock) {
      // Add null check for lockUntil
      const lockActive = globalWsInstance.lockUntil !== null && Date.now() < globalWsInstance.lockUntil;
      if (lockActive) {
        const unlockTime = globalWsInstance.lockUntil ? new Date(globalWsInstance.lockUntil).toISOString() : 'N/A';
        console.log(`[useLiveAPI] CONNECTION LOCKED: Will not create new connections until ${unlockTime}`);
        return Promise.resolve(); // Resolve silently, don't throw error
      } else {
        // Lock expired, reset it
        console.log(`[useLiveAPI] Connection lock expired, allowing connection.`);
        globalWsInstance.connectionLock = false;
        globalWsInstance.lockUntil = null;
      }
    }

    // Check if already connected via the global instance
    if (globalWsInstance.ws && globalWsInstance.ws.readyState === WebSocket.OPEN && globalWsInstance.url === client.url) {
      // If force reconnecting, disconnect the existing connection
      if (isForceReconnect) {
        console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Force reconnect - disconnecting existing connection`);
        
        try {
          if (globalWsInstance.ws.readyState === WebSocket.OPEN || globalWsInstance.ws.readyState === WebSocket.CONNECTING) {
            globalWsInstance.ws.close(1000, "Force reconnect after restart");
          }
        } catch (closeErr) {
          console.error(`[useLiveAPI] Error closing WebSocket during force reconnect:`, closeErr);
        }
        
        // Reset global state immediately
        globalWsInstance.ws = null;
        globalWsInstance.isConnecting = false;
        globalWsInstance.clientId = null;
      } 
      // Otherwise, if the existing connection belongs to this client instance, reuse it
      else if (globalWsInstance.clientId === connectionInstanceIdRef.current) {
        console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Reusing existing WebSocket connection owned by this client.`);
        // Ensure client.ws points to the global instance
        if (client.ws !== globalWsInstance.ws) {
          console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Updating client.ws reference.`);
          client.ws = globalWsInstance.ws;
        }
        setConnected(true); // Ensure UI state is correct
        return Promise.resolve();
      } else {
        // If the connection belongs to another client, disconnect it first
        // This scenario might indicate a component lifecycle issue
        console.warn(`[useLiveAPI] [${connectionInstanceIdRef.current}] Global WebSocket exists but belongs to another client (${globalWsInstance.clientId}). Disconnecting old connection before creating new one.`);
        // Attempt to close the old connection gracefully
        try {
          if (globalWsInstance.ws.readyState === WebSocket.OPEN || globalWsInstance.ws.readyState === WebSocket.CONNECTING) {
            globalWsInstance.ws.close(1000, "New connection requested by different client");
          }
        } catch (closeErr) {
          console.error("Error closing old WebSocket:", closeErr);
        }
        // Reset global state immediately
        globalWsInstance.ws = null;
        globalWsInstance.isConnecting = false;
        globalWsInstance.clientId = null;
      }
    }

    // --- Start WebSocket Connection ---
    try {
      console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Creating NEW global WebSocket connection`);
      globalWsInstance.isConnecting = true;
      globalWsInstance.clientId = connectionInstanceIdRef.current; // Claim the connection
      globalWsInstance.url = client.url; // Store URL
      globalWsInstance.config = config; // Store config
      // Removed references to connectionCount and lastConnectionTime

      const ws = new WebSocket(client.url);
      globalWsInstance.ws = ws;
      globalWsInstance.isConnecting = false; // Reset state for new connection

      // Store in the client (maybe redundant now, but kept for compatibility)
      client.ws = ws;

      // Connection Timeout
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          console.error(`[useLiveAPI] [${connectionInstanceIdRef.current}] Connection timeout after 10 seconds`);
          globalWsInstance.isConnecting = false;
          if (globalWsInstance.ws === ws) { // Ensure we only clear if it's the current attempt
            globalWsInstance.ws = null;
            globalWsInstance.clientId = null;
          }
          setConnected(false);
        }
      }, 10000);

      // Set up event handlers for the WebSocket
      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] WebSocket opened`);
        globalWsInstance.isConnecting = false;

        try {
          const currentConfig = config || {}; // Capture config state at time of connection
          
          // Get the accessToken from localStorage
          const accessToken = localStorage.getItem('mocktrail-token');
          
          // Generate or use existing sessionId
          let sessionId = sessionStorage.getItem('interview_session_id');
          if (!sessionId) {
            // Create a new session ID that combines interviewDetails.id (if available) with a unique identifier
            sessionId = `${interviewDetails.userInterviewId || ''}`;
            sessionStorage.setItem('interview_session_id', sessionId);
          }
          
          console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Using sessionId: ${sessionId}`);
          console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Interview Details Received:`, JSON.stringify(interviewDetails, null, 2));
          
          const backendConfig = {
            interviewDetails: {
              id: interviewDetails?.id || '',
              userInterviewId: interviewDetails?.userInterviewId || '',
              userName: interviewDetails?.userName || '',
              company: interviewDetails?.company || '',
              jobRole: interviewDetails?.jobRole || '',
              interviewTitle: interviewDetails?.interviewTitle || '',
              language: interviewDetails?.language || '',
              jobDescription: interviewDetails?.jobDescription || '',
              durationMinutes: interviewDetails?.durationMinutes || '',
              voicePack: interviewDetails?.voicePack || '',
              characterGender: interviewDetails?.characterGender || '',
              interviewPrompt: interviewDetails?.interviewPrompt || '',
              series: interviewDetails?.series || '',
              resume: interviewDetails?.resume || '',
              callbackUrl: interviewDetails?.callbackUrl || '',
              screening: interviewDetails?.screening ?? undefined,
              screeningToken: interviewDetails?.screeningToken || '',
              webhookToken: interviewDetails?.webhookToken || '',
              implementor_unique_id: implementorUniqueId || '', // Add implementor unique ID for custom interview config
            },
            systemPrompt: currentConfig.systemInstruction?.parts[0]?.text || '', // This prompt is not used on backend proxy, this is just passed. NOTE: This is old prompt as compared to proxy one.
            googleSearch: currentConfig.googleSearch ?? true,
            allowInterruptions: currentConfig.allowInterruptions ?? false,
            accessToken: accessToken, // Add the accessToken to the config
            sessionId: sessionId // This is the scheduled user interview id
          };
          
          console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Built Backend Config:`, JSON.stringify(backendConfig, null, 2));
          
          // This is the .setup config message
          const configMessage = JSON.stringify({
            type: 'config',
            config: backendConfig
          });

          console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Sending config message:`, configMessage);
          ws.send(configMessage);
          console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Config message sent`);

          // Don't set connected here - wait for setupcomplete event to ensure full initialization
          
          // Emit the setupcomplete event *after* config is sent and state is updated
          setTimeout(() => {
            // Check if the WebSocket is still open before emitting setupcomplete
            if (ws.readyState === WebSocket.OPEN) {
              onSetupComplete();
            } else {
              console.warn(`[useLiveAPI] [${connectionInstanceIdRef.current}] WebSocket closed before setup completion`);
            }
          }, 50); // Short delay

        } catch (error) {
          console.error(`[useLiveAPI] [${connectionInstanceIdRef.current}] Error sending config:`, error);
          setConnected(false);
          globalWsInstance.isConnecting = false;
          if (globalWsInstance.ws === ws) {
             globalWsInstance.ws = null;
             globalWsInstance.clientId = null;
          }
          ws.close(); // Close the connection if config fails
        }
      };

      // Reverted ws.onmessage to handle stringified JSON with type: 'audio'
      ws.onmessage = (event) => {
        const messageType = typeof event.data === 'string' ? 'string' : event.data instanceof ArrayBuffer ? 'ArrayBuffer' : 'other';
        console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Received message of type: ${messageType}`);

        try {
          if (typeof event.data === 'string') {
            // console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Processing string message`);
            const data = JSON.parse(event.data);

            // Check for AUDIO message type - GOOGLE OFFICIAL IMPLEMENTATION
            if (data.type === 'audio' && typeof data.data === 'string') {
              // Convert base64 to ArrayBuffer (from backend)
              const arrayBuffer = base64ToArrayBuffer(data.data);
              // Process as PCM16
              const pcm16Data = new Uint8Array(arrayBuffer);
              console.log(`[AudioStreamer] 📥 Audio chunk: ${pcm16Data.length} bytes (${(pcm16Data.length / 48).toFixed(1)}ms at 24kHz)`);
              addPCM16(pcm16Data); // Use official-style function
            }
            // Check for interruption message
            else if (data.type === 'interrupted') {
              console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Received interruption event from server`);
              // Immediately stop audio playback
              stopAudioStreamer();
              // Notify client about interruption
              client.emit("interrupted");
            }
            // Check for status: connected message
            else if (data.status === 'connected') {
              console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Received connected status from server`);
              setConnected(true);
            }
            // Check for text response
            else if (data.type === 'text' && data.data) {
              console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Received text message: ${data.data.substring(0, 30)}...`);
              client.emit("content", { 
                modelTurn: { 
                  parts: [{ text: data.data }] 
                } 
              });
            }
            // Handle text_message response (structured text from AI via function calling)
            else if (data.type === 'text_message' && data.data) {
              console.group(`%c[useLiveAPI] 📨 text_message received`, 'color: #a855f7; font-weight: bold');
              console.log(`Connection: ${connectionInstanceIdRef.current}`);
              console.log(`Type: ${data.data.message_type}`);
              console.log(`Language: ${data.data.language ?? 'N/A'}`);
              console.log(`Message length: ${data.data.message?.length || 0}`);
              console.log(`Message preview:`, data.data.message?.substring(0, 150));
              console.log(`Full data:`, data.data);
              console.groupEnd();
              // Emit as a special content event that includes the structured data
              client.emit("content", { 
                modelTurn: { 
                  parts: [{ 
                    text: data.data.message,
                    textMessageData: data.data // Include the full structured data
                  } as any] 
                } 
              });
              console.log(`[useLiveAPI] ✅ text_message emitted as content event`);
            }
            // Handle transcription (AI speech)
            else if (data.type === 'transcription' && data.data) {
              console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Received transcription: ${data.data.substring(0, 30)}...`);
              client.emit("transcription", data.data);
            }
            // Handle outputTranscription separately (match format from main.py)
            else if (data.type === 'outputTranscription' && data.data) {
              console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Received outputTranscription: ${data.data.substring(0, 30)}...`);
              client.emit("transcription", data.data);
            }
            // Handle input transcription (user speech)
            else if (data.type === 'input_transcription' || data.type === 'user_transcription' && data.data) {
              console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Received input transcription: ${data.data.substring(0, 30)}...`);
              client.emit("input_transcription", data.data);
            }
            // Handle turn_complete signal from server
            else if (data.type === 'turn_complete') {
              console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Received turn_complete signal from server.`);
              // Emit the turncomplete event to ensure all listeners are notified
              client.emit("turncomplete");
              
              // Log that we're handling turn completion
              console.log(`[useLiveAPI] [${connectionInstanceIdRef.current}] Resetting transcription state after turn completion`);
            }
          }
        } catch (error) {
          console.error(`[useLiveAPI] [${connectionInstanceIdRef.current}] Error processing message:`, error);
        }
      };

    } catch (error) {
      console.error(`[useLiveAPI] [${connectionInstanceIdRef.current}] Error connecting to WebSocket:`, error);
      setConnected(false);
      globalWsInstance.isConnecting = false;
      if (globalWsInstance.clientId === connectionInstanceIdRef.current) {
         globalWsInstance.ws = null;
         globalWsInstance.clientId = null;
      }
    }
  }, [client, connectionInstanceIdRef, config, setConnected, onSetupComplete]);

  // At the end, ensure we return the right type
  const result: UseLiveAPIResults & { interruptPlayback: () => void } = {
    client: client as ExtendedMultimodalLiveClient, // Assert non-null for return type
    setConfig,
    config: config as LiveConfig, // Assert non-null for return type
    connected,
    connect,
    disconnect,
    volume,
    interruptPlayback
  };

  return result;
}
